'use client';

import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Group, Input, Tooltip } from '@mantine/core';
import { DateTimePicker, DateValue } from '@mantine/dates';
import { useDebouncedCallback } from '@mantine/hooks';
import { PostResponse } from '@/queries/posts/usePost';
import { useUpdatePost } from '@/queries/posts/useUpdatePost';
import { Icon } from '@/ui';

export const PostDate = ({
  post,
  label,
  withArchiveButton,
}: {
  post: PostResponse;
  label?: string;
  withArchiveButton?: boolean;
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<DateValue>(
    dayjs.utc(post.post.date).isValid() ? dayjs.utc(post.post.date).format() : null
  );
  const { mutate: updatePost } = useUpdatePost();
  const debouncedUpdatePost = useDebouncedCallback(
    (value: string) => {
      updatePost({ id: post.post.id, date: dayjs(value).utc().toDate() });
    },
    {
      delay: 300,
      flushOnUnmount: true,
    }
  );

  const handleChange = useCallback(
    (value: DateValue) => {
      setDate(value);
      debouncedUpdatePost(dayjs(value).utc().format());
    },
    [debouncedUpdatePost]
  );

  return (
    <Input.Wrapper label={label}>
      <Group gap="xs">
        <DateTimePicker
          placeholder={t('posts.post-date')}
          value={date}
          onChange={handleChange}
          valueFormat="L LT"
          style={{ flex: 1 }}
        />
        {withArchiveButton && (
          <Tooltip label={t('posts.unschedule')}>
            <ActionIcon
              variant="subtle"
              onClick={() =>
                updatePost({
                  id: post.post.id,
                  archived: true,
                  scheduledAt: null,
                  date: null,
                })
              }
            >
              <Icon name="archive" />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Input.Wrapper>
  );
};
